import React, { useState, useEffect } from 'react';
import { 
  Search, Package, Clock, CheckCircle, Truck, AlertCircle, RefreshCw, ChevronLeft,
  Calendar, Filter, Archive, ChevronDown, MapPin, Eye, Phone, MessageSquare,
  CreditCard, Box, Store, User, Navigation, Image as ImageIcon
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const OrdersPortal = ({ onBack, user, onTrackOrder }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [archiveFilter, setArchiveFilter] = useState('active'); // 'active', 'day', 'week', 'month', 'year', 'all'
  const [selectedOrder, setSelectedOrder] = useState(null);
  
  const token = localStorage.getItem('auth_token');
  const isAdmin = user?.role === 'admin';
  const isDriver = user?.role === 'driver';

  // Status configuration
  const statusConfig = {
    'pending': { color: 'bg-yellow-100 text-yellow-700', icon: Clock, label: 'Pending' },
    'payment_confirmed': { color: 'bg-green-100 text-green-700', icon: CreditCard, label: 'Payment Confirmed' },
    'picking': { color: 'bg-orange-100 text-orange-700', icon: Package, label: 'Picking' },
    'picked': { color: 'bg-blue-100 text-blue-700', icon: Box, label: 'Packed' },
    'ready_for_delivery': { color: 'bg-purple-100 text-purple-700', icon: Truck, label: 'Ready for Delivery' },
    'assigned': { color: 'bg-indigo-100 text-indigo-700', icon: User, label: 'Driver Assigned' },
    'driver_to_store': { color: 'bg-indigo-100 text-indigo-700', icon: Store, label: 'Driver to Store' },
    'driver_at_store': { color: 'bg-indigo-100 text-indigo-700', icon: MapPin, label: 'At Store' },
    'out_for_delivery': { color: 'bg-blue-100 text-blue-700', icon: Truck, label: 'Out for Delivery' },
    'delivered': { color: 'bg-green-100 text-green-700', icon: CheckCircle, label: 'Delivered' },
    'ready_for_pickup': { color: 'bg-teal-100 text-teal-700', icon: Store, label: 'Ready for Pickup' },
    'picked_up': { color: 'bg-green-100 text-green-700', icon: CheckCircle, label: 'Picked Up' },
    'cancelled': { color: 'bg-red-100 text-red-700', icon: AlertCircle, label: 'Cancelled' },
  };

  // Archive filter options
  const archiveOptions = [
    { value: 'active', label: 'Active Orders' },
    { value: 'day', label: 'Today' },
    { value: 'week', label: 'This Week' },
    { value: 'month', label: 'This Month' },
    { value: 'year', label: 'This Year' },
    { value: 'all', label: 'All Orders' },
  ];

  // Fetch orders
  const fetchOrders = async () => {
    if (!token) return;
    setLoading(true);
    
    try {
      const res = await fetch(`${API_URL}/api/orders`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setOrders(data);
      } else {
        toast.error('Failed to fetch orders');
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error('Error loading orders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
    // Poll for updates every 30 seconds
    const interval = setInterval(fetchOrders, 30000);
    return () => clearInterval(interval);
  }, []);

  // Filter orders based on search, status, and archive period
  const filterOrders = () => {
    let filtered = [...orders];
    
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(order => 
        (order.order_number && order.order_number.toLowerCase().includes(query)) ||
        order.id.toLowerCase().includes(query) ||
        (order.delivery_info?.name && order.delivery_info.name.toLowerCase().includes(query))
      );
    }
    
    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(order => order.status === statusFilter);
    }
    
    // Archive filter
    const now = new Date();
    const completedStatuses = ['delivered', 'picked_up', 'cancelled'];
    
    if (archiveFilter === 'active') {
      filtered = filtered.filter(order => !completedStatuses.includes(order.status));
    } else if (archiveFilter !== 'all') {
      // Only show completed orders for archive views
      filtered = filtered.filter(order => completedStatuses.includes(order.status));
      
      filtered = filtered.filter(order => {
        const orderDate = new Date(order.created_at);
        const diffMs = now - orderDate;
        const diffDays = diffMs / (1000 * 60 * 60 * 24);
        
        switch (archiveFilter) {
          case 'day':
            return diffDays < 1;
          case 'week':
            return diffDays < 7;
          case 'month':
            return diffDays < 30;
          case 'year':
            return diffDays < 365;
          default:
            return true;
        }
      });
    }
    
    // Sort by date (newest first)
    filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    
    return filtered;
  };

  const filteredOrders = filterOrders();

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD'
    }).format(amount || 0);
  };

  // Check if order is trackable
  const isTrackable = (status) => {
    return ['assigned', 'driver_to_store', 'driver_at_store', 'out_for_delivery'].includes(status);
  };

  // Get order timeline
  const getOrderTimeline = (order) => {
    const timeline = [];
    const history = order.status_history || [];
    
    // Add creation
    timeline.push({
      status: 'Order Placed',
      time: order.created_at,
      completed: true
    });
    
    // Add status history
    history.forEach(item => {
      const config = statusConfig[item.status];
      if (config) {
        timeline.push({
          status: config.label,
          time: item.timestamp,
          completed: true,
          notes: item.notes
        });
      }
    });
    
    return timeline;
  };

  // Render status badge
  const renderStatusBadge = (status) => {
    const config = statusConfig[status] || statusConfig['pending'];
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </Badge>
    );
  };

  // Order Detail View
  if (selectedOrder) {
    const timeline = getOrderTimeline(selectedOrder);
    
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Button 
          variant="ghost" 
          onClick={() => setSelectedOrder(null)}
          className="mb-4 flex items-center gap-1 text-gray-600 hover:text-gray-900"
        >
          <ChevronLeft className="w-5 h-5" />
          Back to Orders
        </Button>

        <div className="space-y-6">
          {/* Order Header */}
          <Card className="p-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
              <div>
                <h1 className="text-2xl font-bold text-gray-800">
                  Order #{selectedOrder.order_number || selectedOrder.id.slice(0, 8).toUpperCase()}
                </h1>
                <p className="text-gray-500">{formatDate(selectedOrder.created_at)}</p>
              </div>
              <div className="flex items-center gap-3">
                {renderStatusBadge(selectedOrder.status)}
                {isTrackable(selectedOrder.status) && onTrackOrder && (
                  <Button 
                    onClick={() => onTrackOrder(selectedOrder.id)}
                    className="bg-blue-500 hover:bg-blue-600 text-white"
                  >
                    <Navigation className="w-4 h-4 mr-2" />
                    Track Live
                  </Button>
                )}
              </div>
            </div>

            {/* Order Timeline */}
            <div className="mb-6">
              <h3 className="font-semibold text-gray-800 mb-4">Order Progress</h3>
              <div className="space-y-3">
                {timeline.map((step, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                      step.completed ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-500'
                    }`}>
                      {step.completed ? (
                        <CheckCircle className="w-4 h-4" />
                      ) : (
                        <Clock className="w-4 h-4" />
                      )}
                    </div>
                    <div className="flex-1 pb-3 border-b border-gray-100">
                      <p className="font-medium text-gray-800">{step.status}</p>
                      <p className="text-sm text-gray-500">{formatDate(step.time)}</p>
                      {step.notes && (
                        <p className="text-sm text-gray-600 mt-1 italic">{step.notes}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Card>

          {/* Order Details Grid */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* Customer Info */}
            <Card className="p-6">
              <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <User className="w-5 h-5 text-blue-500" />
                Customer Information
              </h3>
              <div className="space-y-2">
                <p><span className="text-gray-500">Name:</span> {selectedOrder.delivery_info?.name || 'N/A'}</p>
                <p><span className="text-gray-500">Email:</span> {selectedOrder.delivery_info?.email || 'N/A'}</p>
                <p><span className="text-gray-500">Phone:</span> {selectedOrder.delivery_info?.phone || 'N/A'}</p>
                {selectedOrder.order_type === 'delivery' && (
                  <>
                    <p><span className="text-gray-500">Address:</span> {selectedOrder.delivery_info?.address || 'N/A'}</p>
                    <p><span className="text-gray-500">City:</span> {selectedOrder.delivery_info?.city || 'N/A'}</p>
                    <p><span className="text-gray-500">Postal Code:</span> {selectedOrder.delivery_info?.postal_code || 'N/A'}</p>
                  </>
                )}
              </div>
            </Card>

            {/* Payment Info */}
            <Card className="p-6">
              <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-green-500" />
                Payment Summary
              </h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-500">Subtotal:</span>
                  <span>{formatCurrency(selectedOrder.subtotal)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Tax:</span>
                  <span>{formatCurrency(selectedOrder.tax)}</span>
                </div>
                {selectedOrder.delivery_fee > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Delivery Fee:</span>
                    <span>{formatCurrency(selectedOrder.delivery_fee)}</span>
                  </div>
                )}
                <div className="flex justify-between font-bold pt-2 border-t">
                  <span>Total:</span>
                  <span>{formatCurrency(selectedOrder.total)}</span>
                </div>
                <div className="mt-3 pt-3 border-t">
                  <p className="text-sm text-gray-500">Payment Method: <span className="capitalize font-medium">{selectedOrder.payment_method}</span></p>
                  <p className="text-sm text-gray-500">Payment Status: <span className="capitalize font-medium">{selectedOrder.payment_status}</span></p>
                </div>
              </div>
            </Card>
          </div>

          {/* Order Items */}
          <Card className="p-6">
            <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <Package className="w-5 h-5 text-purple-500" />
              Order Items ({selectedOrder.items?.length || 0})
            </h3>
            <div className="space-y-3">
              {selectedOrder.items?.map((item, index) => (
                <div key={index} className="flex items-center justify-between py-2 border-b border-gray-100">
                  <div>
                    <p className="font-medium text-gray-800">{item.name}</p>
                    <p className="text-sm text-gray-500">Qty: {item.quantity} × {formatCurrency(item.price)}</p>
                  </div>
                  <p className="font-medium">{formatCurrency(item.quantity * item.price)}</p>
                </div>
              ))}
            </div>
          </Card>

          {/* Proof of Delivery */}
          {selectedOrder.proof_of_delivery && (
            <Card className="p-6">
              <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <ImageIcon className="w-5 h-5 text-teal-500" />
                Proof of Delivery
              </h3>
              <div className="grid grid-cols-2 gap-4">
                {selectedOrder.proof_of_delivery.photo1 && (
                  <img 
                    src={selectedOrder.proof_of_delivery.photo1} 
                    alt="Proof 1" 
                    className="w-full h-48 object-cover rounded-lg"
                  />
                )}
                {selectedOrder.proof_of_delivery.photo2 && (
                  <img 
                    src={selectedOrder.proof_of_delivery.photo2} 
                    alt="Proof 2" 
                    className="w-full h-48 object-cover rounded-lg"
                  />
                )}
              </div>
              {selectedOrder.proof_of_delivery.notes && (
                <p className="mt-3 text-gray-600 italic">Note: {selectedOrder.proof_of_delivery.notes}</p>
              )}
              <p className="mt-2 text-sm text-gray-500">
                Delivered at: {formatDate(selectedOrder.proof_of_delivery.timestamp)}
              </p>
            </Card>
          )}

          {/* Driver Info */}
          {selectedOrder.driver_id && (
            <Card className="p-6">
              <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <Truck className="w-5 h-5 text-blue-500" />
                Driver Information
              </h3>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                  <User className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="font-medium">{selectedOrder.driver_name || 'Driver Assigned'}</p>
                  <div className="flex gap-2 mt-2">
                    <Button size="sm" variant="outline">
                      <Phone className="w-4 h-4 mr-1" />
                      Call
                    </Button>
                    <Button size="sm" variant="outline">
                      <MessageSquare className="w-4 h-4 mr-1" />
                      Message
                    </Button>
                  </div>
                </div>
              </div>
            </Card>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {onBack && (
        <Button 
          variant="ghost" 
          onClick={onBack}
          className="mb-4 flex items-center gap-1 text-gray-600 hover:text-gray-900"
        >
          <ChevronLeft className="w-5 h-5" />
          Back to Shop
        </Button>
      )}
      
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
            <Package className="w-8 h-8 text-blue-500" />
            Orders Portal
          </h1>
          <p className="text-gray-500 mt-1">
            {isAdmin ? 'Manage all orders' : isDriver ? 'Track your deliveries' : 'Track your orders'}
          </p>
        </div>
        <Button variant="outline" onClick={fetchOrders} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <Card className="p-4 mb-6">
        <div className="flex flex-wrap gap-4">
          {/* Search */}
          <div className="flex-1 min-w-[250px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search by order number or customer name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="order-search-input"
              />
            </div>
          </div>
          
          {/* Status Filter */}
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px]" data-testid="status-filter">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {Object.entries(statusConfig).map(([key, config]) => (
                <SelectItem key={key} value={key}>{config.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {/* Archive Filter */}
          <Select value={archiveFilter} onValueChange={setArchiveFilter}>
            <SelectTrigger className="w-[180px]" data-testid="archive-filter">
              <Archive className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Archive" />
            </SelectTrigger>
            <SelectContent>
              {archiveOptions.map(option => (
                <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </Card>

      {/* Orders List */}
      {loading ? (
        <Card className="p-8 text-center">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto text-blue-500 mb-4" />
          <p className="text-gray-500">Loading orders...</p>
        </Card>
      ) : filteredOrders.length === 0 ? (
        <Card className="p-8 text-center">
          <Package className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-800">No Orders Found</h3>
          <p className="text-gray-500">
            {archiveFilter === 'active' 
              ? 'No active orders at the moment.' 
              : 'No orders match your filters.'}
          </p>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredOrders.map(order => {
            const config = statusConfig[order.status] || statusConfig['pending'];
            const Icon = config.icon;
            
            return (
              <Card 
                key={order.id} 
                className="p-4 hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => setSelectedOrder(order)}
                data-testid={`order-card-${order.id}`}
              >
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${config.color}`}>
                      <Icon className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-800">
                        #{order.order_number || order.id.slice(0, 8).toUpperCase()}
                      </h3>
                      <p className="text-sm text-gray-500">{formatDate(order.created_at)}</p>
                      <p className="text-sm text-gray-600">{order.delivery_info?.name || 'Customer'}</p>
                    </div>
                  </div>
                  
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    <div className="text-right">
                      <p className="font-bold text-lg text-gray-800">{formatCurrency(order.total)}</p>
                      <p className="text-sm text-gray-500">{order.items?.length || 0} items</p>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {renderStatusBadge(order.status)}
                      
                      {isTrackable(order.status) && onTrackOrder && (
                        <Button 
                          size="sm"
                          variant="outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            onTrackOrder(order.id);
                          }}
                          className="text-blue-600"
                        >
                          <Navigation className="w-4 h-4" />
                        </Button>
                      )}
                      
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedOrder(order);
                        }}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Summary Stats */}
      {!loading && orders.length > 0 && (
        <Card className="mt-6 p-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            <div>
              <p className="text-2xl font-bold text-gray-800">{orders.length}</p>
              <p className="text-sm text-gray-500">Total Orders</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-yellow-600">
                {orders.filter(o => !['delivered', 'picked_up', 'cancelled'].includes(o.status)).length}
              </p>
              <p className="text-sm text-gray-500">Active</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-green-600">
                {orders.filter(o => ['delivered', 'picked_up'].includes(o.status)).length}
              </p>
              <p className="text-sm text-gray-500">Completed</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-blue-600">
                {formatCurrency(orders.reduce((sum, o) => sum + (o.total || 0), 0))}
              </p>
              <p className="text-sm text-gray-500">Total Value</p>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};

export default OrdersPortal;
